import React, { useState, useEffect } from 'react';
import {

    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import { User } from './UsersPage';
import { allRoles } from '../../login/authentication';
import { useFirebaseService } from '../../../data/FirebaseService';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { set } from 'date-fns';

interface EditUserDialogProps {
    open: boolean;
    user: User | null;
    onClose: () => void;
    onSave?: (updatedUser: User) => Promise<void> | void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
    open,
    user,
    onClose,
    onSave
}) => {
    const [displayName, setDisplayName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [role, setRole] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const firebaseService = useFirebaseService();

    // Initialize form when user data changes
    useEffect(() => {
        if (user) {
            setDisplayName(user.name || '');
            setRole(user.roles[0] || '');
        }
    }, [user]);

    const handleSubmit = async () => {
        if (!user) return;

        setErrorMessage('');
        setIsSubmitting(true);

        user.name = displayName;
        user.roles = [role];

        try {
            if (onSave && firebaseService) {
                const updateUser = httpsCallable(firebaseService.functions, "updateUser");
                const result = await updateUser(user);

                console.log("Firebase response:", result.data);
                // If the response is okay
                toast.success("User updated successfully!");
                await onSave(user);
            }
            handleClose();
        } catch (error) {
            setErrorMessage("Failed to update user.");
            console.error('Error saving user:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setDisplayName('');
        setRole('');
        setErrorMessage('');
        onClose();
    };

    if (!user) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Email"
                        value={user.email}
                        fullWidth
                        disabled
                        variant="filled"
                    />

                    <TextField
                        label="Display Name"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        fullWidth
                        autoFocus
                    />

                    <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={role}
                            label="Role"
                            onChange={(e) => setRole(e.target.value)}
                        >
                            {Object.entries(allRoles).filter(([roleKey]) => roleKey !== 'viewer').map(([roleKey, roleValue]) => (
                                <MenuItem key={roleKey} value={roleKey}>
                                    {roleValue}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                {errorMessage && (
                    <Typography variant="body1" color="error" style={{ marginTop: 16 }}>
                        {errorMessage}
                    </Typography>)}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary" disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUserDialog;